/**
 * UniFi Protect Integration Store
 *
 * Zustand store for UniFi Protect camera system state.
 * Supports multiple NVRs per owner — each NVR has its own connection state,
 * camera list, event log, and health panel.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';
import { api } from '@/services/api/client';
import { useAuthStore } from '@/stores/auth/auth-store';

// ============================================================================
// Types
// ============================================================================

export interface UnifiCamera {
  nvrId: string;  // which NVR this camera belongs to
  id: string;
  name: string;
  type: string;
  mac: string;
  host: string;
  state: string;
  isConnected: boolean;
  isMotionDetected: boolean;
  lastMotion?: number;
  lastRing?: number;
  hasMic: boolean;
  hasSpeaker: boolean;
  rtspEnabled: boolean;
  featureFlags: {
    hasChime: boolean;
    hasBluetooth: boolean;
    hasPackageCamera: boolean;
    canOpticalZoom: boolean;
    smartDetectTypes: string[];
  };
}

export interface UnifiEvent {
  id: string;
  nvrId?: string;
  type: 'motion' | 'smartDetectZone' | 'ring' | 'audioDetect';
  cameraId: string;
  cameraName?: string;
  start: number;
  end?: number;
  score?: number;
  smartDetectTypes?: string[];
  audioType?: string;
}

export interface UnifiNvrInfo {
  id: string;
  host: string;
  name: string;
  version: string;
  uptime: number;
  cameraCount: number;
}

export interface UnifiNvrHealth {
  name: string;
  version: string;
  hardwarePlatform?: string;
  uptime: number;
  storageUsed: number;
  storageTotal: number;
  storageAvailable: number;
  retentionDays: number;
  cameraCount: number;
}

export type PtzAction = 'pan-left' | 'pan-right' | 'tilt-up' | 'tilt-down' | 'zoom-in' | 'zoom-out' | 'stop';

export interface NvrConnectionState {
  nvrId: string;
  name: string;
  host: string;
  hasCredentials: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isLoading: boolean;
  nvrInfo?: UnifiNvrInfo;
  cameras: UnifiCamera[];
  snapshotUrls: Record<string, string>;
  events: UnifiEvent[];
  nvrHealth: UnifiNvrHealth | null;
  eventThumbnails: Record<string, string>;
  error: string | null;
}

interface TalkbackSession {
  sessionId: string;
  nvrId: string;
  cameraId: string;
  audioWs: WebSocket;
  mediaRecorder: MediaRecorder;
  stream: MediaStream;
}

interface LivestreamEntry {
  sessionId: string;
  ws: WebSocket;
  mediaSource: MediaSource;
  sourceBuffer: SourceBuffer | null;
  objectUrl: string;
  pendingChunks: ArrayBuffer[];
}

const MAX_EVENTS = 50;
const API_BASE = '/integrations/unifi-protect';
const WS_PATH = '/api/v1/integrations/unifi-protect';

function initNvrState(nvrId: string, name: string, host: string): NvrConnectionState {
  return {
    nvrId, name, host,
    hasCredentials: false, isConnected: false, isConnecting: false, isLoading: false,
    cameras: [], snapshotUrls: {}, events: [], nvrHealth: null, eventThumbnails: {}, error: null,
  };
}

interface UnifiProtectState {
  // Per-NVR state keyed by nvrId
  nvrs: Record<string, NvrConnectionState>;
  isLoadingNvrs: boolean;

  // Global (span all NVRs)
  activeTalkback: TalkbackSession | null;
  isTalking: boolean;
  livestreams: Record<string, LivestreamEntry>;   // cameraId → entry
  isStreaming: Record<string, boolean>;           // cameraId → bool

  // Actions
  loadNvrs: () => Promise<void>;
  addNvr: (name: string, host: string, username: string, password: string, port?: number) => Promise<void>;
  updateNvr: (nvrId: string, name: string, host: string, username: string, password: string, port?: number) => Promise<void>;
  removeNvr: (nvrId: string) => Promise<void>;
  connectNvr: (nvrId: string) => Promise<void>;
  loadCameras: (nvrId: string) => Promise<void>;
  fetchSnapshot: (nvrId: string, cameraId: string) => Promise<string | null>;
  fetchNvrHealth: (nvrId: string) => Promise<void>;
  loadEvents: (nvrId: string, cameraId?: string, type?: string) => Promise<void>;
  fetchEventThumbnail: (nvrId: string, eventId: string) => Promise<string | null>;
  ptzMove: (nvrId: string, cameraId: string, action: PtzAction) => Promise<void>;
  startTalkback: (nvrId: string, cameraId: string) => Promise<void>;
  stopTalkback: () => Promise<void>;
  startLivestream: (nvrId: string, cameraId: string, channel?: number) => Promise<string>;
  stopLivestream: (cameraId: string) => void;
  handleCameraStatusEvent: (payload: UnifiCamera & { nvrId: string }) => void;
  handleProtectEvent: (payload: UnifiEvent & { nvrId: string }) => void;
  clearNvrError: (nvrId: string) => void;
}

// ============================================================================
// Store
// ============================================================================

export const useUnifiProtectStore = create<UnifiProtectState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      nvrs: {},
      isLoadingNvrs: false,
      activeTalkback: null,
      isTalking: false,
      livestreams: {},
      isStreaming: {},

      loadNvrs: async () => {
        set((state) => { state.isLoadingNvrs = true; });
        try {
          const response = await api.get<Array<{ nvrId: string; name: string; host: string; hasCredentials: boolean; isConnected: boolean; nvrInfo?: UnifiNvrInfo; cameraCount?: number }>>(
            `${API_BASE}/nvrs`
          );
          const nvrList = response.data ?? [];
          set((state) => {
            for (const nvr of nvrList) {
              if (!state.nvrs[nvr.nvrId]) {
                state.nvrs[nvr.nvrId] = initNvrState(nvr.nvrId, nvr.name, nvr.host);
              }
              state.nvrs[nvr.nvrId].hasCredentials = nvr.hasCredentials;
              state.nvrs[nvr.nvrId].isConnected = nvr.isConnected;
              state.nvrs[nvr.nvrId].nvrInfo = nvr.nvrInfo;
              state.nvrs[nvr.nvrId].name = nvr.name;
              state.nvrs[nvr.nvrId].host = nvr.host;
            }
            state.isLoadingNvrs = false;
          });
        } catch (error) {
          set((state) => { state.isLoadingNvrs = false; });
        }
      },

      addNvr: async (name, host, username, password, port) => {
        const response = await api.post<{ nvrId: string; hasCredentials: boolean; isConnected: boolean; cameraCount: number }>(
          `${API_BASE}/nvrs`,
          { name, host, username, password, ...(port ? { port } : {}) }
        );
        const { nvrId, hasCredentials, isConnected } = response.data ?? {};
        if (!nvrId) throw new Error('Failed to create NVR');

        set((state) => {
          state.nvrs[nvrId] = initNvrState(nvrId, name, host);
          state.nvrs[nvrId].hasCredentials = hasCredentials ?? true;
          state.nvrs[nvrId].isConnected = isConnected ?? false;
        });

        if (isConnected) {
          await get().loadCameras(nvrId);
        }
      },

      updateNvr: async (nvrId, name, host, username, password, port) => {
        set((state) => {
          if (state.nvrs[nvrId]) state.nvrs[nvrId].isConnecting = true;
        });
        try {
          const response = await api.put<{ nvrId: string; hasCredentials: boolean; isConnected: boolean }>(
            `${API_BASE}/nvrs/${nvrId}`,
            { name, host, username, password, ...(port ? { port } : {}) }
          );
          const { isConnected } = response.data ?? {};
          set((state) => {
            if (state.nvrs[nvrId]) {
              state.nvrs[nvrId].name = name;
              state.nvrs[nvrId].host = host;
              state.nvrs[nvrId].hasCredentials = true;
              state.nvrs[nvrId].isConnected = isConnected ?? false;
              state.nvrs[nvrId].isConnecting = false;
            }
          });
          if (isConnected) {
            await get().loadCameras(nvrId);
          }
        } catch (error) {
          set((state) => {
            if (state.nvrs[nvrId]) {
              state.nvrs[nvrId].error = error instanceof Error ? error.message : 'Update failed';
              state.nvrs[nvrId].isConnecting = false;
            }
          });
          throw error;
        }
      },

      removeNvr: async (nvrId) => {
        await api.delete(`${API_BASE}/nvrs/${nvrId}`);
        set((state) => {
          delete state.nvrs[nvrId];
        });
      },

      connectNvr: async (nvrId) => {
        set((state) => {
          if (state.nvrs[nvrId]) {
            state.nvrs[nvrId].isConnecting = true;
            state.nvrs[nvrId].error = null;
          }
        });
        try {
          await api.post(`${API_BASE}/nvrs/${nvrId}/connect`);
          await get().loadNvrs();
          await get().loadCameras(nvrId);
          set((state) => {
            if (state.nvrs[nvrId]) state.nvrs[nvrId].isConnecting = false;
          });
        } catch (error) {
          const msg = error instanceof Error
            ? error.message
            : (typeof error === 'object' && error !== null && 'message' in error)
              ? String((error as any).message)
              : 'Connection failed';
          set((state) => {
            if (state.nvrs[nvrId]) {
              state.nvrs[nvrId].error = msg;
              state.nvrs[nvrId].isConnecting = false;
            }
          });
          throw error;
        }
      },

      loadCameras: async (nvrId) => {
        set((state) => {
          if (state.nvrs[nvrId]) state.nvrs[nvrId].isLoading = true;
        });
        try {
          const response = await api.get<UnifiCamera[]>(`${API_BASE}/nvrs/${nvrId}/cameras`);
          set((state) => {
            if (state.nvrs[nvrId]) {
              state.nvrs[nvrId].cameras = response.data ?? [];
              state.nvrs[nvrId].isLoading = false;
            }
          });
        } catch (error) {
          set((state) => {
            if (state.nvrs[nvrId]) {
              state.nvrs[nvrId].error = error instanceof Error ? error.message : 'Failed to load cameras';
              state.nvrs[nvrId].isLoading = false;
            }
          });
        }
      },

      fetchSnapshot: async (nvrId: string, cameraId: string) => {
        const nvr = get().nvrs[nvrId];
        if (!nvr) return null;

        const prev = nvr.snapshotUrls[cameraId];
        if (prev) URL.revokeObjectURL(prev);

        try {
          const response = await fetch(
            `/api/v1${API_BASE}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/snapshot`,
            { credentials: 'include' }
          );
          if (!response.ok) return null;

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          set((state) => {
            if (state.nvrs[nvrId]) state.nvrs[nvrId].snapshotUrls[cameraId] = objectUrl;
          });
          return objectUrl;
        } catch {
          return null;
        }
      },

      fetchNvrHealth: async (nvrId: string) => {
        try {
          const response = await api.get<UnifiNvrHealth>(`${API_BASE}/nvrs/${nvrId}/nvr`);
          set((state) => {
            if (state.nvrs[nvrId]) state.nvrs[nvrId].nvrHealth = response.data ?? null;
          });
        } catch {
          // non-critical
        }
      },

      loadEvents: async (nvrId: string, cameraId?: string, type?: string) => {
        try {
          const params = new URLSearchParams();
          if (cameraId) params.set('cameraId', cameraId);
          if (type) params.set('type', type);
          const qs = params.toString();
          const response = await api.get<UnifiEvent[]>(`${API_BASE}/nvrs/${nvrId}/events${qs ? '?' + qs : ''}`);
          set((state) => {
            if (state.nvrs[nvrId]) state.nvrs[nvrId].events = response.data ?? [];
          });
        } catch {
          // ignore
        }
      },

      fetchEventThumbnail: async (nvrId: string, eventId: string): Promise<string | null> => {
        const nvr = get().nvrs[nvrId];
        if (!nvr) return null;
        const cached = nvr.eventThumbnails[eventId];
        if (cached) return cached;
        try {
          const response = await fetch(
            `/api/v1${API_BASE}/nvrs/${nvrId}/events/${encodeURIComponent(eventId)}/thumbnail`,
            { credentials: 'include' }
          );
          if (!response.ok) return null;
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          set((state) => {
            if (state.nvrs[nvrId]) state.nvrs[nvrId].eventThumbnails[eventId] = url;
          });
          return url;
        } catch {
          return null;
        }
      },

      ptzMove: async (nvrId: string, cameraId: string, action: PtzAction) => {
        try {
          await api.post(`${API_BASE}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/ptz`, { action });
        } catch {
          // PTZ errors are non-critical
        }
      },

      startTalkback: async (nvrId: string, cameraId: string) => {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        } catch {
          throw new Error('Microphone access denied. Allow microphone access and try again.');
        }

        const sessionResponse = await api.post<{ sessionId: string }>(
          `${API_BASE}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/talkback/start`
        );
        const sessionId = sessionResponse.data?.sessionId;
        if (!sessionId) throw new Error('Failed to start talkback session.');

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = window.location.host;
        const audioWs = new WebSocket(
          `${wsProtocol}//${wsHost}${WS_PATH}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/talkback/audio?sessionId=${sessionId}`
        );

        await new Promise<void>((resolve, reject) => {
          audioWs.onopen = () => resolve();
          audioWs.onerror = () => reject(new Error('Audio WebSocket connection failed'));
          setTimeout(() => reject(new Error('Audio WebSocket timed out')), 5000);
        });

        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0 && audioWs.readyState === WebSocket.OPEN) {
            e.data.arrayBuffer().then(buf => audioWs.send(buf));
          }
        };
        mediaRecorder.start(100);

        set((state) => {
          state.activeTalkback = { sessionId, nvrId, cameraId, audioWs, mediaRecorder, stream } as any;
          state.isTalking = true;
        });
      },

      stopTalkback: async () => {
        const session = get().activeTalkback;
        if (!session) return;

        try {
          session.mediaRecorder.stop();
          session.stream.getTracks().forEach(t => t.stop());
          session.audioWs.close();

          await api.post(
            `${API_BASE}/nvrs/${session.nvrId}/cameras/${encodeURIComponent(session.cameraId)}/talkback/stop`,
            { sessionId: session.sessionId }
          ).catch(() => {});
        } catch {
          // cleanup errors are non-fatal
        }

        set((state) => {
          state.activeTalkback = null;
          state.isTalking = false;
        });
      },

      startLivestream: async (nvrId: string, cameraId: string, channel = 0): Promise<string> => {
        get().stopLivestream(cameraId);

        const response = await api.post<{ sessionId: string; channel: number }>(
          `${API_BASE}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/livestream/start`,
          { channel }
        );
        const sessionId = response.data?.sessionId;
        if (!sessionId) throw new Error('Failed to obtain livestream session.');

        const ms = new MediaSource();
        const objectUrl = URL.createObjectURL(ms);
        let sourceBuffer: SourceBuffer | null = null;
        const pendingChunks: ArrayBuffer[] = [];
        let pendingCodec: string | null = null;
        let msOpen = false;

        const flushPending = () => {
          if (!sourceBuffer || sourceBuffer.updating || pendingChunks.length === 0) return;
          const chunk = pendingChunks.shift()!;
          try { sourceBuffer.appendBuffer(chunk); } catch {}
        };

        const tryCreateSourceBuffer = (codec: string) => {
          if (!msOpen || ms.readyState !== 'open') return;
          try {
            sourceBuffer = ms.addSourceBuffer(codec);
            sourceBuffer.addEventListener('updateend', flushPending);
            set((state) => {
              if (state.livestreams[cameraId]) state.livestreams[cameraId].sourceBuffer = sourceBuffer;
            });
            flushPending();
          } catch {}
        };

        ms.addEventListener('sourceopen', () => {
          msOpen = true;
          if (pendingCodec) { tryCreateSourceBuffer(pendingCodec); pendingCodec = null; }
        }, { once: true });

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = window.location.host;
        const userId = useAuthStore.getState().user?.id ?? '';

        const wsUrl = `${wsProtocol}//${wsHost}${WS_PATH}/nvrs/${nvrId}/cameras/${encodeURIComponent(cameraId)}/livestream?sessionId=${sessionId}&channel=${channel}&ownerType=User&ownerId=${userId}&nvrId=${nvrId}`;

        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        let codecReceived = false;

        ws.onmessage = (event) => {
          if (!codecReceived && typeof event.data === 'string') {
            try {
              const msg: unknown = JSON.parse(event.data);
              if (msg != null && typeof msg === 'object' && 'type' in msg && 'codec' in msg) {
                const msgObj = msg as Record<string, unknown>;
                if (msgObj.type === 'codec' && typeof msgObj.codec === 'string') {
                  codecReceived = true;
                  if (msOpen) { tryCreateSourceBuffer(msgObj.codec); }
                  else { pendingCodec = msgObj.codec; }
                }
              }
            } catch {}
            return;
          }

          if (event.data instanceof ArrayBuffer) {
            if (sourceBuffer && !sourceBuffer.updating) {
              try { sourceBuffer.appendBuffer(event.data); } catch {}
            } else {
              pendingChunks.push(event.data);
            }
          }
        };

        ws.onclose = () => {
          set((state) => {
            delete state.livestreams[cameraId];
            state.isStreaming[cameraId] = false;
          });
        };

        ws.onerror = () => { get().stopLivestream(cameraId); };

        set((state) => {
          state.livestreams[cameraId] = { sessionId, ws: ws as any, mediaSource: ms, sourceBuffer: null, objectUrl, pendingChunks };
          state.isStreaming[cameraId] = true;
        });

        return objectUrl;
      },

      stopLivestream: (cameraId: string) => {
        const entry = get().livestreams[cameraId];
        if (!entry) return;

        try { entry.ws.close(); } catch {}
        try { URL.revokeObjectURL(entry.objectUrl); } catch {}

        set((state) => {
          delete state.livestreams[cameraId];
          state.isStreaming[cameraId] = false;
        });
      },

      handleCameraStatusEvent: (payload: UnifiCamera & { nvrId: string }) => {
        const { nvrId } = payload;
        set((state) => {
          const nvr = state.nvrs[nvrId];
          if (!nvr) return;
          const idx = nvr.cameras.findIndex(c => c.id === payload.id);
          if (idx >= 0) {
            nvr.cameras[idx] = { ...nvr.cameras[idx], ...payload };
          }
        });
      },

      handleProtectEvent: (payload: UnifiEvent & { nvrId: string }) => {
        const { nvrId } = payload;
        set((state) => {
          const nvr = state.nvrs[nvrId];
          if (!nvr) return;
          nvr.events.unshift(payload);
          if (nvr.events.length > MAX_EVENTS) {
            nvr.events = nvr.events.slice(0, MAX_EVENTS);
          }
        });
      },

      clearNvrError: (nvrId: string) => {
        set((state) => {
          if (state.nvrs[nvrId]) state.nvrs[nvrId].error = null;
        });
      },
    }))
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

// Stable empty-array sentinel — prevents new reference on every render when NVR
// has no data yet, satisfying React 18's useSyncExternalStore snapshot stability.
const EMPTY_CAMERAS: UnifiCamera[] = [];
const EMPTY_EVENTS: UnifiEvent[] = [];

// useShallow ensures Object.values() doesn't create a new array reference when
// the contents of nvrs haven't changed (React 18 snapshot-stability requirement).
export const useNvrList = () => useUnifiProtectStore(useShallow(s => Object.values(s.nvrs)));
export const useNvr = (nvrId: string) => useUnifiProtectStore(useShallow(s => s.nvrs[nvrId]));
export const useNvrCameras = (nvrId: string) => useUnifiProtectStore(s => s.nvrs[nvrId]?.cameras ?? EMPTY_CAMERAS);
export const useNvrEvents = (nvrId: string) => useUnifiProtectStore(s => s.nvrs[nvrId]?.events ?? EMPTY_EVENTS);
export const useNvrHealth = (nvrId: string) => useUnifiProtectStore(s => s.nvrs[nvrId]?.nvrHealth ?? null);
export const useIsLoadingNvrs = () => useUnifiProtectStore(s => s.isLoadingNvrs);

// Aliases for index re-exports
export const useUnifiProtectCameras = (nvrId: string) => useUnifiProtectStore(s => s.nvrs[nvrId]?.cameras ?? EMPTY_CAMERAS);
export const useUnifiProtectEvents = (nvrId: string) => useUnifiProtectStore(s => s.nvrs[nvrId]?.events ?? EMPTY_EVENTS);
export const useUnifiProtectConnection = (nvrId: string) => useUnifiProtectStore(useShallow(s => {
  const nvr = s.nvrs[nvrId];
  if (!nvr) return null;
  return { isConnected: nvr.isConnected, isConnecting: nvr.isConnecting, error: nvr.error };
}));
